import { normLocation } from './pnl.js';
import { isSeriesBranchRule } from './salary-settings.js';
import { applyTax, DEFAULT_TAX_PROFILE } from './tax.js';
const round2 = (n) => Math.round((n + Number.EPSILON) * 100) / 100;
/** Cap on a percentage bonus OR deduction. Either one larger than the salary
 *  it's a percentage of is far more likely a typo (1000 for 100) than an intent,
 *  and the damage of accepting one is a wildly overpaid — or unpaid — coach. */
export const ONE_TIME_BONUS_MAX_PERCENT = 100;
/** Longest reason text accepted for a bonus/deduction. It's a label on a
 *  payslip line ("השתתפות בסדנת TRX"), not a paragraph. */
export const ADJUSTMENT_NOTE_MAX_LENGTH = 120;
/** Normalise an entered reason: trimmed, capped, and empty ⇒ null (so "no
 *  reason" is one value everywhere rather than null / '' / '   '). */
export function normalizeAdjustmentNote(note) {
    const t = (note ?? '').trim();
    return t ? t.slice(0, ADJUSTMENT_NOTE_MAX_LENGTH) : null;
}
/** Shared label builder for the two per-line adjustments. The percentage is
 *  annotated when the amount was entered as one — so "(10%)" reads as a
 *  percentage rather than an unexplained ₪ — and the operator's reason follows
 *  it, which is the whole point of the reason: the coach sees what the money
 *  was for on their own payslip, without asking. */
function adjustmentLabel(base, percent, note) {
    const pct = clampBonusPercent(percent);
    const head = pct > 0 ? `${base} (${Number(pct.toFixed(2))}%)` : base;
    const reason = normalizeAdjustmentNote(note);
    return reason ? `${head} — ${reason}` : head;
}
/** The one-time bonus's display label, used on every surface (report, drawer,
 *  PDF, Excel, approval page) so they always read alike. */
export function oneTimeBonusLabel(percent, note) {
    return adjustmentLabel('בונוס חד-פעמי', percent, note);
}
/** The one-time deduction's display label — the bonus's mirror image. The ₪ it
 *  labels is rendered negative by the caller; the label itself never carries a
 *  sign, so it reads the same in a table cell as on a payslip line. */
export function deductionLabel(percent, note) {
    return adjustmentLabel('הפחתה', percent, note);
}
/** Clamp a percentage bonus/deduction to [0..100]; absent/non-finite is 0. */
export function clampBonusPercent(pct) {
    const n = Number(pct ?? 0);
    if (!Number.isFinite(n) || n <= 0)
        return 0;
    return Math.min(ONE_TIME_BONUS_MAX_PERCENT, Math.round(n * 100) / 100);
}
/** Day of week for a YYYY-MM-DD string: 0=Sun … 5=Fri, 6=Sat. */
function dowOf(dateISO) {
    const [y, m, d] = dateISO.split('-').map(Number);
    return new Date(Date.UTC(y, m - 1, d)).getUTCDay();
}
const FRIDAY = 5;
const SATURDAY = 6;
/** A worked Friday or Saturday counts as a weekend workday (for per-weekend-
 *  workday travel). The Israeli weekend is Fri+Sat — independent of the bonus. */
function isWeekendDate(dateISO) {
    const dow = dowOf(dateISO);
    return dow === FRIDAY || dow === SATURDAY;
}
/** The fixed ₪ supplement a single meeting earns from the per-class bonus rules.
 *  A rule matches when its location matches AND its name equals the meeting's
 *  class_name OR its box_category (Arbox: box_category = service = class name),
 *  AND — when the rule lists days — the meeting fell on one of them. A
 *  day-specific rule beats an every-day rule for the same class, so "₪20 on
 *  Pilates, ₪50 on Saturdays" pays ₪50 on Saturday, not ₪70.
 *  Case/space-insensitive. Shared by the engine (period total) and the detail
 *  builder (per-meeting in-row amount) so the two always reconcile. */
export function classBonusForMeeting(meeting, rules) {
    if (!rules?.length)
        return 0;
    const norm = (s) => (s ?? '').trim().toLowerCase();
    const names = new Set([norm(meeting.class_name), norm(meeting.box_category)]);
    const loc = norm(meeting.location);
    const dow = dowOf(meeting.date);
    const matching = rules.filter((r) => norm(r.location) === loc && names.has(norm(r.class_name)));
    const byDay = matching.find((r) => r.days?.length && r.days.includes(dow));
    const everyDay = matching.find((r) => !r.days?.length);
    return (byDay ?? everyDay)?.amount ?? 0;
}
/** The rule a meeting is paid by under the per-קטגוריה rates: the coach's rule
 *  for the meeting's קטגוריה at the meeting's branch, else the every-branch rule
 *  for it, else none. A rule with a category_id matches by id ONLY (survives a
 *  rename in Arbox); an id-less (older) rule matches by name, case/space-insensitive. */
export function categoryRateRuleFor(meeting, rules) {
    if (!rules?.length)
        return null;
    const norm = (s) => (s ?? '').trim().toLowerCase();
    const cat = norm(meeting.box_category);
    const id = meeting.box_category_id ?? null;
    const mine = rules.filter((r) => r.category_id != null ? id != null && r.category_id === id : !!cat && norm(r.category) === cat);
    const loc = normLocation(meeting.location);
    return (mine.find((r) => r.location != null && normLocation(r.location) === loc) ??
        mine.find((r) => r.location == null) ??
        null);
}
/** The bracket a headcount falls in. A count outside every bracket never falls
 *  back to Arbox's salary (that could pay LESS for a fuller class): it takes the
 *  nearest bracket below it, or the lowest bracket when it's under all of them. */
export function categoryRateTierFor(tiers, count) {
    if (!tiers.length)
        return null;
    const sorted = [...tiers].sort((a, b) => a.min_participants - b.min_participants);
    const inside = sorted.find((t) => count >= t.min_participants && (t.max_participants == null || count <= t.max_participants));
    if (inside)
        return inside;
    const below = sorted.filter((t) => t.min_participants <= count);
    return below[below.length - 1] ?? sorted[0] ?? null;
}
/** What a meeting pays under the coach's per-קטגוריה rates, or null when no rule
 *  matches (⇒ keep Arbox's salary). */
export function categoryRateForMeeting(meeting, rules, method) {
    const rule = categoryRateRuleFor(meeting, rules);
    if (!rule)
        return null;
    const tier = categoryRateTierFor(rule.tiers, paidCountForMethod(meeting, method));
    return tier ? round2(tier.amount) : null;
}
/** HH:MM out of whatever Arbox sent ("18:00", "18:00:00", "2026-09-06 18:00:00"). */
export function normStartTime(t) {
    const m = String(t ?? '').match(/(\d{1,2}):(\d{2})/);
    return m ? `${m[1].padStart(2, '0')}:${m[2]}` : null;
}
/** The series-rate rule a meeting belongs to, or null. A rule that carries a
 *  series_id matches by id ONLY (the class may have moved — that's the point of
 *  the id); rules saved before the id existed match by SLOT — class type +
 *  branch + weekday + start time (see SeriesRateRuleSchema). */
export function seriesRateRuleFor(meeting, rules) {
    if (!rules?.length)
        return null;
    const slotRules = rules.filter((r) => !isSeriesBranchRule(r));
    if (meeting.series_id != null) {
        const byId = slotRules.find((r) => r.series_id != null && r.series_id === meeting.series_id);
        if (byId)
            return byId;
    }
    const norm = (x) => (x ?? '').trim().toLowerCase();
    const cls = norm(meeting.class_name);
    const time = normStartTime(meeting.time);
    if (!cls || !time)
        return null;
    const loc = normLocation(meeting.location);
    const dow = dowOf(meeting.date);
    return (slotRules.find((r) => r.series_id == null &&
        norm(r.class_name) === cls &&
        normLocation(r.location) === loc &&
        r.weekday === dow &&
        normStartTime(r.start_time) === time) ?? null);
}
/** The branch rate a SERIES meeting (one with an Arbox series_id) gets at its
 *  current branch, or null. One-off classes never match. If a list somehow
 *  holds two rules for a branch, the first wins (the editor flags it). */
export function seriesBranchRateRuleFor(meeting, rules) {
    if (!rules?.length || meeting.series_id == null)
        return null;
    const loc = normLocation(meeting.location);
    if (!loc)
        return null;
    return rules.find((r) => isSeriesBranchRule(r) && normLocation(r.location) === loc) ?? null;
}
/** What a meeting pays under our own rates, most specific first: its series,
 *  then the branch rate for every series at its branch, then its קטגוריה
 *  (branch, then all branches). null ⇒ no rule ⇒ Arbox's salary. */
export function classRateFor(meeting, rules, method) {
    const series = seriesRateRuleFor(meeting, rules.series_rates);
    if (series) {
        const tier = categoryRateTierFor(series.tiers, paidCountForMethod(meeting, method));
        if (tier)
            return { amount: round2(tier.amount), source: 'series' };
    }
    const branch = seriesBranchRateRuleFor(meeting, rules.series_rates);
    if (branch) {
        const tier = categoryRateTierFor(branch.tiers, paidCountForMethod(meeting, method));
        if (tier)
            return { amount: round2(tier.amount), source: 'branch' };
    }
    const cat = categoryRateForMeeting(meeting, rules.category_rates, method);
    return cat == null ? null : { amount: cat, source: 'category' };
}
const hasClassRates = (r) => !!(r.series_rates?.length || r.category_rates?.length);
/** Replace Arbox's per-meeting salary with our own rate (series → קטגוריה)
 *  wherever one matches, marking the meeting `rate_source` and keeping Arbox's
 *  figure in `arbox_salary`. Applied ONCE, right where a coach's meetings leave
 *  the fetch — before the engine, the P&L split, the detail/PDF/Excel — so every
 *  consumer of `salary` sees the same number. Co-taught meetings are already
 *  split per coach, so each coach's share is replaced (or kept) on its own. */
export function applyClassRates(meetings, rules, method) {
    if (!hasClassRates(rules))
        return meetings;
    return meetings.map((m) => {
        const rate = classRateFor(m, rules, method);
        return rate == null ? m : { ...m, salary: rate.amount, arbox_salary: m.salary, rate_source: rate.source };
    });
}
/** applyClassRates for a cancelled meeting's detail row, whose would-have-paid
 *  fee lives in `full_salary`. A cancelled class has no attendance, so a
 *  bracketed rate is read at 0 participants (its lowest bracket). */
export function applyClassRatesToCancelled(meetings, rules, method) {
    if (!hasClassRates(rules))
        return meetings;
    return meetings.map((m) => {
        const rate = classRateFor(m, rules, method);
        return rate == null ? m : { ...m, full_salary: rate.amount };
    });
}
/** Sort + bound a ladder into brackets: each tier runs from its own from_class up
 *  to the class before the next tier's, and the last one is open-ended. Tiers are
 *  deduped by from_class (the LAST one entered for a number wins, which is what a
 *  user editing a row expects) and anything below class 1 is dropped. */
export function normalizeVolumeTiers(tiers) {
    const byStart = new Map();
    for (const t of tiers ?? []) {
        const from = Math.floor(Number(t?.from_class));
        const amount = Number(t?.amount);
        if (!Number.isFinite(from) || from < 1)
            continue;
        byStart.set(from, Number.isFinite(amount) && amount > 0 ? amount : 0);
    }
    return [...byStart.entries()]
        .sort((a, b) => a[0] - b[0])
        .map(([from_class, amount]) => ({ from_class, amount }));
}
/** The ladder resolved against a class count — one entry per bracket, in order.
 *  `classes`/`pay` are 0 for brackets the coach never reached, so the same list
 *  can render the full ladder or just the paying part (filter on pay > 0). */
export function volumeBonusTierBreakdown(classCount, tiers) {
    const sorted = normalizeVolumeTiers(tiers);
    const count = Math.max(0, Math.floor(Number(classCount) || 0));
    return sorted.map((t, i) => {
        const next = sorted[i + 1];
        const to_class = next ? next.from_class - 1 : null;
        // How many of the coach's classes land in [from_class .. to_class].
        const upper = to_class == null ? count : Math.min(count, to_class);
        const classes = Math.max(0, upper - t.from_class + 1);
        return {
            from_class: t.from_class,
            to_class,
            amount: t.amount,
            classes,
            pay: round2(classes * t.amount),
        };
    });
}
/** What the volume-bonus ladder pays for a class count. 0 when it's off or no
 *  bracket was reached. MARGINAL: each class pays its own bracket's rate. */
export function volumeBonusFor(classCount, v) {
    if (!v?.enabled)
        return 0;
    return round2(volumeBonusTierBreakdown(classCount, v.tiers).reduce((sum, b) => sum + b.pay, 0));
}
/** The volume bonus's display label, annotated with the class count that earned
 *  it — "בונוס לפי כמות שיעורים חודשית (45)" — so the ₪ isn't an unexplained
 *  number in the report, the PDF, the Excel or the coach's approval page. The
 *  name says "מדרגות" nowhere: coaches read this label, and the ladder is the
 *  mechanism, not the thing they're being paid for. */
export function volumeBonusLabel(classCount) {
    const n = Math.max(0, Math.floor(Number(classCount ?? 0)) || 0);
    return n > 0
        ? `בונוס לפי כמות שיעורים חודשית (${n})`
        : 'בונוס לפי כמות שיעורים חודשית';
}
/** Sort + dedupe a daily-adjustment ladder: tiers below daily count 1 are
 *  dropped, and duplicate from_count rows collapse to the LAST one entered
 *  (what a user editing a row expects). Unlike normalizeVolumeTiers, the sign
 *  of `adjustment` is kept — this ladder can deduct, not just add. */
export function normalizeDailyClassAdjustmentTiers(tiers) {
    const byStart = new Map();
    for (const t of tiers ?? []) {
        const from = Math.floor(Number(t?.from_count));
        const adjustment = Number(t?.adjustment);
        if (!Number.isFinite(from) || from < 1)
            continue;
        byStart.set(from, Number.isFinite(adjustment) ? adjustment : 0);
    }
    return [...byStart.entries()]
        .sort((a, b) => a[0] - b[0])
        .map(([from_count, adjustment]) => ({ from_count, adjustment }));
}
/** How many of `meetings` fall on each date — the basis for the daily
 *  class-count adjustment (and reusable anywhere else a per-day count is
 *  needed). Meetings should already be the PAID set (no empty/cancelled). */
export function countMeetingsByDay(meetings) {
    const byDay = new Map();
    for (const m of meetings)
        byDay.set(m.date, (byDay.get(m.date) ?? 0) + 1);
    return byDay;
}
/** The tier a day's paid-class count reaches: the one with the HIGHEST
 *  from_count ≤ dayCount. NOT marginal — only this single tier applies, to
 *  every class that day. undefined when the day doesn't reach even the lowest
 *  tier. */
export function dailyClassAdjustmentTierFor(dayCount, tiers) {
    const sorted = normalizeDailyClassAdjustmentTiers(tiers);
    let matched;
    for (const t of sorted) {
        if (t.from_count > dayCount)
            break;
        matched = t;
    }
    return matched;
}
/** What a single meeting earns (or loses) from the daily class-count
 *  adjustment: its OWN day's tier, applied per class. Shared by the engine
 *  (period total) and the detail builder (per-meeting in-row amount) so the
 *  two always reconcile — same role as classBonusForMeeting. */
export function dailyClassAdjustmentForMeeting(meeting, dayCounts, tiers) {
    const dayCount = dayCounts.get(meeting.date) ?? 0;
    return dailyClassAdjustmentTierFor(dayCount, tiers)?.adjustment ?? 0;
}
/** The daily class-count adjustment's display label. Unlike volumeBonusLabel,
 *  it carries no count suffix — the total sums many different days, each with
 *  its own count and tier, so no single number describes it. */
export function dailyClassAdjustmentLabel() {
    return 'התאמת שכר לפי כמות שיעורים ביום';
}
/** The per-meeting count the box gets PAID on, per its class_calc_method. A
 *  meeting is "empty" when this is 0 (see the empty-class-pay feature). */
export function paidCountForMethod(m, method) {
    const reg = m.registration_count ?? 0;
    const chk = m.checkin_count ?? 0;
    const late = m.late_cancel_count ?? 0;
    switch (method) {
        case 'registered':
            return reg;
        case 'registered_with_late_cancel':
            return reg + late;
        case 'checkin_with_late_cancel':
            return chk + late;
        case 'checkin':
        default:
            return chk;
    }
}
/** The % of the fee a cancelled class pays: the rule for its cancellation reason,
 *  or the catch-all `default_percent` when that reason has no rule of its own —
 *  which is also what a cancellation recorded with NO reason gets. Shared by the
 *  engine (period total) and the detail builder (per-meeting in-row amount) so the
 *  two always reconcile. */
export function percentForCancelReason(reasonId, rules, defaultPercent) {
    const id = Number(reasonId);
    if (Number.isFinite(id) && id) {
        const rule = rules?.find((r) => Number(r.cancel_reason_id) === id);
        if (rule)
            return rule.percent;
    }
    return defaultPercent;
}
/** What a single cancelled meeting pays this coach. 0 when the box doesn't pay
 *  for cancellations, or the reason's percentage is 0. */
export function cancelledClassPayForMeeting(m, policy, rules) {
    if (!policy?.enabled)
        return 0;
    const pct = percentForCancelReason(m.cancel_reason_id, rules, policy.default_percent);
    if (!(pct > 0))
        return 0;
    // A negative fee can't turn into a payment.
    return round2(Math.max(0, m.salary || 0) * (pct / 100));
}
/** Whole-day difference (b − a) for two YYYY-MM-DD dates. */
function daysBetween(aISO, bISO) {
    const [ay, am, ad] = aISO.split('-').map(Number);
    const [by, bm, bd] = bISO.split('-').map(Number);
    const a = Date.UTC(ay, am - 1, ad);
    const b = Date.UTC(by, bm - 1, bd);
    return Math.round((b - a) / 86_400_000);
}
/** Whether a trial lead qualifies for the conversion bonus: it converted AND the
 *  conversion happened on/after the trial within `windowDays`. Shared by the
 *  engine (period total) and the detail builder (per-conversion in-row amount) so
 *  the two always reconcile. A window of 0 means "same day only". */
export function conversionQualifies(c, windowDays) {
    if (!c.converted || !c.conversion_date)
        return false;
    if (!/^\d{4}-\d{2}-\d{2}$/.test(c.trial_date) || !/^\d{4}-\d{2}-\d{2}$/.test(c.conversion_date)) {
        return false;
    }
    const gap = daysBetween(c.trial_date, c.conversion_date);
    return gap >= 0 && gap <= windowDays;
}
/** Sort + dedupe a conversion-bonus ladder: tiers below count 1 are dropped,
 *  non-positive amounts become 0, and duplicate from_count rows collapse to the
 *  LAST one entered (what a user editing a row expects). */
export function normalizeConversionTiers(tiers) {
    const byStart = new Map();
    for (const t of tiers ?? []) {
        const from = Math.floor(Number(t?.from_count));
        const amount = Number(t?.amount);
        if (!Number.isFinite(from) || from < 1)
            continue;
        byStart.set(from, Number.isFinite(amount) && amount > 0 ? amount : 0);
    }
    return [...byStart.entries()]
        .sort((a, b) => a[0] - b[0])
        .map(([from_count, amount]) => ({ from_count, amount }));
}
/** The conversion tier a coach's period count reaches — the one with the
 *  HIGHEST from_count ≤ count — plus where it ends (the count before the next
 *  tier; null for the open-ended top one). undefined when the count doesn't
 *  reach even the lowest tier. */
export function conversionTierFor(count, tiers) {
    const sorted = normalizeConversionTiers(tiers);
    let idx = -1;
    for (let i = 0; i < sorted.length; i++) {
        if (sorted[i].from_count > count)
            break;
        idx = i;
    }
    if (idx < 0)
        return undefined;
    const next = sorted[idx + 1];
    return { ...sorted[idx], to_count: next ? next.from_count - 1 : null };
}
/** What the conversion ladder pays for `count` qualifying conversions. NOT
 *  marginal: every conversion is paid at the reached tier's rate. */
export function conversionBonusFor(count, tiers) {
    const n = Math.max(0, Math.floor(Number(count) || 0));
    if (n === 0)
        return 0;
    return round2(n * (conversionTierFor(n, tiers)?.amount ?? 0));
}
/** A conversion tier's range in words — "1–4 המרות", "10 המרות ומעלה" — for the
 *  "which tier did the coach reach" line in the drawer / PDF / Excel. */
export function conversionTierRangeLabel(t) {
    if (t.to_count == null)
        return t.from_count === 1 ? 'מהמרה אחת ומעלה' : `${t.from_count} המרות ומעלה`;
    if (t.to_count === t.from_count)
        return t.from_count === 1 ? 'המרה אחת' : `${t.from_count} המרות`;
    return `${t.from_count}–${t.to_count} המרות`;
}
/** The full "which tier" sentence for the PDF / Excel — "5 המרות · מדרגה:
 *  5–9 המרות · ₪70 להמרה". The drawer builds the same line from its parts. */
export function conversionTierText(t, count) {
    const n = count === 1 ? 'המרה אחת' : `${count} המרות`;
    return `${n} · מדרגה: ${conversionTierRangeLabel(t)} · ₪${t.amount.toLocaleString('he-IL')} להמרה`;
}
/** The conversion bonus's display label, annotated with the count and the
 *  per-conversion rate of the tier reached — "בונוס המרה (5 המרות × ₪70)" — so
 *  the coach can see which tier they hit. The rate is derived from the frozen
 *  total, so it also labels snapshots taken before the ladder existed. */
export function conversionBonusLabel(count, bonus) {
    const n = Math.max(0, Math.floor(Number(count ?? 0)) || 0);
    const total = Number(bonus ?? 0) || 0;
    if (n === 0 || total <= 0)
        return 'בונוס המרה';
    const rate = round2(total / n);
    return `בונוס המרה (${n === 1 ? 'המרה אחת' : `${n} המרות`} × ₪${rate.toLocaleString('he-IL')})`;
}
/** One meeting's weekend bonus (0 on a weekday). In 'percent' mode a % uplift
 *  on the meeting's pay; in 'fixed' mode a flat ₪ per Friday/Saturday session.
 *  The caller decides whether the bonus is enabled and which meetings qualify. */
export function weekendBonusForMeeting(m, w) {
    const dow = dowOf(m.date);
    if (dow !== FRIDAY && dow !== SATURDAY)
        return 0;
    if (w.mode === 'fixed')
        return dow === FRIDAY ? w.friday_amount : w.saturday_amount;
    const pct = dow === FRIDAY ? w.friday_percent : w.saturday_percent;
    return ((m.salary || 0) * pct) / 100;
}
export function computeClassMechanismPay(input) {
    const { settings: s } = input;
    // ── Empty-class split ───────────────────────────────────────────────────
    // When the empty-class-pay feature is on, a meeting that OCCURRED but had 0 of
    // the paid metric (per class_calc_method) is pulled out of regular class pay
    // and paid a flat amount instead. Off ⇒ every meeting is "paid" (unchanged).
    const ecp = s.empty_class_pay;
    const method = input.class_calc_method ?? 'checkin';
    const isEmptyMeeting = (m) => ecp.enabled && paidCountForMethod(m, method) === 0;
    const paidMeetings = ecp.enabled ? input.meetings.filter((m) => !isEmptyMeeting(m)) : input.meetings;
    const empty_class_count = ecp.enabled ? input.meetings.length - paidMeetings.length : 0;
    const empty_class_pay = round2(ecp.enabled ? ecp.amount * empty_class_count : 0);
    // ── Class pay + activity (Arbox-computed per meeting; excludes empty classes) ─
    const class_pay = round2(paidMeetings.reduce((sum, m) => sum + (m.salary || 0), 0));
    const class_count = paidMeetings.length;
    // ── Cancelled classes ───────────────────────────────────────────────────
    // Classes Arbox marked cancelled: they never happened, so they're outside
    // `meetings` entirely (no class pay, no weekend bonus, no class bonus, no
    // workday → no travel). The box may still owe part of the fee depending on WHY
    // it was cancelled — a percentage per reason, 0 by default.
    let cancelled_class_pay = 0;
    let cancelled_class_count = 0;
    if (s.cancelled_class_pay?.enabled) {
        for (const m of input.cancelled_meetings ?? []) {
            const pay = cancelledClassPayForMeeting(m, s.cancelled_class_pay, input.cancel_reason_rules);
            if (pay <= 0)
                continue;
            cancelled_class_pay += pay;
            cancelled_class_count++;
        }
        cancelled_class_pay = round2(cancelled_class_pay);
    }
    // ── Weekend bonus — per-meeting, separate Friday / Saturday values. In
    // 'percent' mode it's a % uplift on the meeting's Arbox pay; in 'fixed' mode
    // it's a flat ₪ added per weekend session. (Empty classes are excluded.)
    let weekend_bonus = 0;
    if (s.weekend_bonus.enabled) {
        const w = s.weekend_bonus;
        weekend_bonus = round2(paidMeetings.reduce((sum, m) => sum + weekendBonusForMeeting(m, w), 0));
    }
    // ── Per-class bonus — fixed ₪ per session. box_category = service = class name
    // (Arbox), so a rule matches a meeting by that name (class_name OR box_category)
    // AND location. Case/space-insensitive. (Empty classes are excluded.)
    let class_bonus = 0;
    if (input.class_bonuses?.length) {
        class_bonus = round2(paidMeetings.reduce((sum, m) => sum + classBonusForMeeting(m, input.class_bonuses), 0));
    }
    // ── Volume bonus (בונוס מדרגות) — a marginal ₪-per-class ladder on HOW MANY
    // classes this line paid for. Counted on the paid classes only: empty classes
    // (paid their own flat rate) and cancelled classes are not on the ladder. In a
    // P&L-split box `paidMeetings` is already just that branch's classes, so each
    // branch counts and pays its own tiers.
    const volume_bonus = volumeBonusFor(class_count, s.volume_bonus);
    // ── Daily class-count adjustment — a ± ₪-per-class ladder decided by HOW
    // MANY classes this line paid for on the SAME day. Not marginal: a single
    // tier applies to the whole day. Counted on paid classes only, same basis as
    // volume_bonus/class_count.
    let daily_class_adjustment = 0;
    if (s.daily_class_adjustment?.enabled) {
        const dayCounts = countMeetingsByDay(paidMeetings);
        daily_class_adjustment = round2(paidMeetings.reduce((sum, m) => sum + dailyClassAdjustmentForMeeting(m, dayCounts, s.daily_class_adjustment.tiers), 0));
    }
    // ── Conversion bonus — ₪ per trial lead that converted to a member within
    // the window, at the rate of the ladder tier the period's COUNT reaches (all
    // conversions at that one rate — not marginal). enabled + window are box-wide;
    // the ladder is the resolved per-coach value. Attributed by trial date (so
    // it's in this period already).
    let conversion_bonus = 0;
    let conversion_count = 0;
    const conv = s.conversion_bonus;
    if (conv.enabled && input.conversions?.length) {
        const qualifying = input.conversions.filter((c) => conversionQualifies(c, conv.window_days)).length;
        conversion_bonus = conversionBonusFor(qualifying, conv.tiers);
        // Only count conversions that were actually paid — a coach below the lowest
        // tier (or on an all-zero ladder) earned nothing, same as before.
        conversion_count = conversion_bonus > 0 ? qualifying : 0;
    }
    return {
        class_pay,
        weekend_bonus,
        class_bonus,
        volume_bonus,
        daily_class_adjustment,
        conversion_bonus,
        conversion_count,
        empty_class_pay,
        empty_class_count,
        cancelled_class_pay,
        cancelled_class_count,
        class_count,
    };
}
export function computeGenericPay(input, opts = {}) {
    const { settings: s } = input;
    const workdaySource = opts.workday_source ?? 'meetings';
    // ── Base salary: fixed monthly, or (time_clock only) hourly × clocked hours.
    let base_salary = 0;
    if (s.base_salary.type === 'fixed')
        base_salary = round2(s.base_salary.monthly_amount);
    else if (s.base_salary.type === 'hourly') {
        base_salary = round2(s.base_salary.hourly_rate * (opts.clocked_hours ?? 0));
    }
    // ── Workdays — source depends on which mechanism(s) this staff runs. ────
    const meetingDays = input.meetings.map((m) => m.date);
    const workdaySet = new Set(workdaySource === 'attendance'
        ? (opts.attendance_days ?? [])
        : workdaySource === 'meetings_and_attendance'
            ? [...meetingDays, ...(opts.attendance_days ?? [])]
            : meetingDays);
    const workdays_count = workdaySet.size;
    const weekend_workdays_count = [...workdaySet].filter((d) => isWeekendDate(d)).length;
    // ── Travel (paid per DAY) ───────────────────────────────────────────────
    let travel = 0;
    switch (s.travel.type) {
        case 'fixed_monthly':
            travel = round2(s.travel.monthly_amount);
            break;
        case 'per_workday':
            travel = round2(s.travel.per_day_amount * workdays_count);
            break;
        case 'per_km': {
            // Per-location: for each configured location, rate × (one-way distance × 2
            // round trip) × the coach's distinct meeting-days at that location. Days
            // are counted per location, so a day teaching at two branches pays a round
            // trip to each. Attendance-clock days carry no location, so this always
            // sources from class meetings regardless of workday_source (unchanged).
            const rate = s.travel.rate_per_km;
            const distByLoc = new Map();
            for (const l of input.travel_km_locations ?? [])
                distByLoc.set(normLocation(l.location), l.distance_km);
            const daysByLoc = new Map();
            for (const m of input.meetings) {
                const loc = normLocation(m.location);
                if (!distByLoc.has(loc))
                    continue;
                let set = daysByLoc.get(loc);
                if (!set)
                    daysByLoc.set(loc, (set = new Set()));
                set.add(m.date);
            }
            let sum = 0;
            for (const [loc, days] of daysByLoc)
                sum += rate * (distByLoc.get(loc) ?? 0) * 2 * days.size;
            travel = round2(sum);
            break;
        }
        default:
            travel = 0;
    }
    return { base_salary, travel, workdays_count, weekend_workdays_count };
}
/** Resolve one entered adjustment to ₪. In percent mode it's a percentage of
 *  `base` — this line's other components — never of itself, and a negative base
 *  resolves to 0 rather than to a negative amount. */
function resolveAdjustment(entry, base) {
    const percent = entry.mode === 'percent' ? clampBonusPercent(entry.percent) : 0;
    const amount = entry.mode === 'percent'
        ? round2(Math.max(0, base) * (percent / 100))
        : round2(Math.max(0, entry.amount || 0));
    return { amount, percent, note: normalizeAdjustmentNote(entry.note) };
}
/**
 * Apply the bonus, then the deduction, then tax — the tail every mechanism's
 * pay runs through.
 *
 * Both adjustments are percentages of the SAME base (`componentsBeforeBonus`,
 * i.e. everything the line earned before either was applied), so granting a
 * 10% bonus doesn't enlarge a 5% fine and vice versa — the order the operator
 * happens to enter them in can't change the money.
 *
 * The deduction comes off BEFORE VAT, the mirror of how the bonus goes on
 * before VAT: it reduces what the coach is owed for the period, so it reduces
 * the invoice they issue for it and the ניכוי מס במקור computed on that.
 *
 * A deduction larger than the line's pay is FLOORED at the pay — a payroll line
 * can't pay a negative amount, and a negative total would break the coach's
 * approval/invoice flow downstream. The returned `deduction` is therefore what
 * was actually taken, which may be less than what was typed; the entered figure
 * is kept separately on the report row so the editor still shows it.
 */
export function resolveAdjustmentsAndTax(componentsBeforeBonus, adj, tax) {
    const bonus = resolveAdjustment(adj.bonus, componentsBeforeBonus);
    const entered = resolveAdjustment(adj.deduction, componentsBeforeBonus);
    const payBeforeDeduction = round2(componentsBeforeBonus + bonus.amount);
    const deduction = round2(Math.min(entered.amount, Math.max(0, payBeforeDeduction)));
    const { subtotal, vat, total, withholding_pct, withholding, net_total } = applyTax(round2(payBeforeDeduction - deduction), tax ?? DEFAULT_TAX_PROFILE);
    return {
        one_time_bonus: bonus.amount,
        one_time_bonus_percent: bonus.percent,
        one_time_bonus_note: bonus.note,
        deduction,
        deduction_percent: entered.percent,
        deduction_note: entered.note,
        subtotal,
        vat,
        total,
        withholding_pct,
        withholding,
        net_total,
    };
}
/**
 * Group-classes payroll for one staff member this period. THIN WRAPPER around
 * computeClassMechanismPay + computeGenericPay + resolveAdjustmentsAndTax,
 * reassembled into the original combined shape. See those functions for the
 * pieces; this is the one to keep calling for a group_classes-only staff member.
 */
export function computePayroll(input) {
    const cls = computeClassMechanismPay(input);
    const generic = computeGenericPay(input); // workday_source defaults to 'meetings'
    const componentsBeforeBonus = round2(cls.class_pay +
        cls.weekend_bonus +
        cls.class_bonus +
        cls.volume_bonus +
        cls.daily_class_adjustment +
        cls.conversion_bonus +
        generic.base_salary +
        generic.travel +
        cls.empty_class_pay +
        cls.cancelled_class_pay);
    const bonusAndTax = resolveAdjustmentsAndTax(componentsBeforeBonus, {
        bonus: {
            mode: input.one_time_bonus_mode,
            amount: input.one_time_bonus,
            percent: input.one_time_bonus_percent,
            note: input.one_time_bonus_note,
        },
        deduction: {
            mode: input.deduction_mode,
            amount: input.deduction,
            percent: input.deduction_percent,
            note: input.deduction_note,
        },
    }, input.tax);
    return {
        arbox_users_boxes_id: input.arbox_users_boxes_id,
        class_pay: cls.class_pay,
        weekend_bonus: cls.weekend_bonus,
        class_bonus: cls.class_bonus,
        volume_bonus: cls.volume_bonus,
        daily_class_adjustment: cls.daily_class_adjustment,
        conversion_bonus: cls.conversion_bonus,
        conversion_count: cls.conversion_count,
        base_salary: generic.base_salary,
        travel: generic.travel,
        empty_class_pay: cls.empty_class_pay,
        cancelled_class_pay: cls.cancelled_class_pay,
        ...bonusAndTax,
        workdays_count: generic.workdays_count,
        weekend_workdays_count: generic.weekend_workdays_count,
        class_count: cls.class_count,
        empty_class_count: cls.empty_class_count,
        cancelled_class_count: cls.cancelled_class_count,
        by_location: payByLocation(input),
    };
}
/**
 * Split a line's per-meeting pay by branch (the meeting's Arbox location), for
 * the report's branch filter. It uses the same per-meeting rules as
 * computeClassMechanismPay, so each component summed over every branch equals
 * the line's component — except for meetings without a location, which stay
 * out of every branch. Ladders decided by the whole line (the daily tier is
 * picked from the line's same-day count, not the branch's) keep the line's
 * tier: this shows where the money of the line came from, it never re-prices.
 * Branches come out sorted by name.
 */
export function payByLocation(input) {
    const { settings: s } = input;
    const byLoc = new Map();
    const slice = (location) => {
        const name = (location ?? '').trim().replace(/\s+/g, ' ');
        if (!name)
            return null;
        const key = normLocation(name);
        let b = byLoc.get(key);
        if (!b) {
            b = {
                location: name,
                class_pay: 0,
                weekend_bonus: 0,
                class_bonus: 0,
                daily_class_adjustment: 0,
                empty_class_pay: 0,
                cancelled_class_pay: 0,
                travel: 0,
                class_count: 0,
                empty_class_count: 0,
                cancelled_class_count: 0,
                workdays_count: 0,
                weekend_workdays_count: 0,
                registration_count: 0,
                checkin_count: 0,
                late_cancel_count: 0,
                days: new Set(),
            };
            byLoc.set(key, b);
        }
        return b;
    };
    const ecp = s.empty_class_pay;
    const method = input.class_calc_method ?? 'checkin';
    const isEmpty = (m) => ecp.enabled && paidCountForMethod(m, method) === 0;
    const paidMeetings = input.meetings.filter((m) => !isEmpty(m));
    const dayCounts = countMeetingsByDay(paidMeetings);
    for (const m of input.meetings) {
        const b = slice(m.location);
        if (!b)
            continue;
        b.days.add(m.date);
        b.registration_count += m.registration_count || 0;
        b.checkin_count += m.checkin_count || 0;
        b.late_cancel_count += m.late_cancel_count || 0;
        if (isEmpty(m)) {
            b.empty_class_count++;
            b.empty_class_pay += ecp.amount;
            continue;
        }
        b.class_count++;
        b.class_pay += m.salary || 0;
        if (s.weekend_bonus.enabled)
            b.weekend_bonus += weekendBonusForMeeting(m, s.weekend_bonus);
        if (input.class_bonuses?.length)
            b.class_bonus += classBonusForMeeting(m, input.class_bonuses);
        if (s.daily_class_adjustment?.enabled) {
            b.daily_class_adjustment += dailyClassAdjustmentForMeeting(m, dayCounts, s.daily_class_adjustment.tiers);
        }
    }
    if (s.cancelled_class_pay?.enabled) {
        for (const m of input.cancelled_meetings ?? []) {
            const pay = cancelledClassPayForMeeting(m, s.cancelled_class_pay, input.cancel_reason_rules);
            if (pay <= 0)
                continue;
            const b = slice(m.location);
            if (!b)
                continue;
            b.cancelled_class_pay += pay;
            b.cancelled_class_count++;
        }
    }
    // per_km travel is already per location: rate × round trip × that branch's
    // meeting days (the same formula as computeGenericPay).
    if (s.travel.type === 'per_km') {
        for (const l of input.travel_km_locations ?? []) {
            const b = byLoc.get(normLocation(l.location));
            if (b)
                b.travel = s.travel.rate_per_km * l.distance_km * 2 * b.days.size;
        }
    }
    return [...byLoc.values()]
        .map(({ days, ...b }) => ({
        ...b,
        class_pay: round2(b.class_pay),
        weekend_bonus: round2(b.weekend_bonus),
        class_bonus: round2(b.class_bonus),
        daily_class_adjustment: round2(b.daily_class_adjustment),
        empty_class_pay: round2(b.empty_class_pay),
        cancelled_class_pay: round2(b.cancelled_class_pay),
        travel: round2(b.travel),
        workdays_count: days.size,
        weekend_workdays_count: [...days].filter((d) => isWeekendDate(d)).length,
    }))
        .sort((a, b) => a.location.localeCompare(b.location, 'he'));
}
/** The all-zero rule: a staff member whose computed total is 0 is hidden from
 *  the report and never emailed (spec). */
export function isZeroPayroll(c) {
    return c.total === 0;
}
/** Read the ניכוי מס במקור part of a computation, tolerating snapshots frozen
 *  before the feature existed (JSONB, so those fields are simply absent). Every
 *  consumer of a stored snapshot should go through this rather than reading the
 *  raw fields, so an old closed run reads as "no withholding, net = total"
 *  instead of NaN/undefined. */
export function withholdingOf(c) {
    const withholding = c.withholding ?? 0;
    return {
        withholding_pct: c.withholding_pct ?? 0,
        withholding,
        net_total: c.net_total ?? round2(c.total - withholding),
    };
}
//# sourceMappingURL=payroll.js.map